import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { Avatar } from './Avatar';
import { Submenu, type SubmenuItem } from '../Submenu/Submenu';
import { LogoutIcon } from '../Icons/Logout';
import { ProfileIcon } from '../Icons/Profile';
import SettingTwoIcon from '../Icons/SettingTwo';
import designTokens from '@/design-tokens';

const useIsNarrow = () => {
  const [isNarrow, setIsNarrow] = useState(() => window.innerWidth < 480);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 479px)');
    const handler = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
    mq.addEventListener('change', handler);
    setIsNarrow(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isNarrow;
};

/** The submenu entries this component knows how to render, in display order. */
export type ActiveUserMenuItemId = 'profile' | 'settings' | 'signout';

const DEFAULT_MENU_ITEMS: ActiveUserMenuItemId[] = ['profile', 'settings', 'signout'];

/**
 * The hover background's radius when the avatar is a company mark.
 *
 * `Avatar` rounds a company tile to 25% of its size, so 8px at the medium (32px)
 * size this component always uses. The avatar sits FLUSH against the container's
 * left edge — there is no left padding — so the two corners are coincident, and
 * the default 32px pill around an 8px avatar corner reads as a misfit rather
 * than as a highlight. Both sides use it so the hover state is one coherent
 * rounded rectangle instead of a pill welded to a square.
 *
 * ⚠️ Tied to the avatar's `size="medium"` below. If that changes, this must too.
 */
const COMPANY_HOVER_RADIUS = '8px';

interface ActiveUserProps {
  companyName: string;
  userName: string;
  userImageUrl?: string;
  firstName?: string;
  lastName?: string;
  onProfileClick?: () => void;
  onSettingsClick?: () => void;
  onSignOutClick?: () => void;
  className?: string;

  /**
   * Opt-in avatar shape. Defaults to `user` (circle, two initials).
   *
   * The portal passes `company`, because a buyer's identity IS their company —
   * and an avatar that changed shape once a buyer's locations were merged under
   * one company would be the layout-changes-with-data anti-pattern, one
   * component wide.
   */
  avatarVariant?: 'user' | 'company';

  /**
   * Opt-in subset of the submenu, in the order given. Defaults to all three.
   *
   * A portal buyer has no Profile or Settings surface, and a menu naming places
   * that do not exist is worse than a short menu.
   *
   * Percy: as the portal expands, more nav items are expected to live in this
   * menu — so it is the portal's nav home rather than a one-off. That is why
   * this is an ordered array and not a set of booleans: a new entry joins the
   * union and each caller slots it where it belongs.
   */
  menuItems?: ActiveUserMenuItemId[];
}

export const ActiveUser: React.FC<ActiveUserProps> = ({
  companyName,
  userName,
  userImageUrl,
  firstName = '',
  lastName = '',
  onProfileClick,
  onSettingsClick,
  onSignOutClick,
  className = '',
  avatarVariant = 'user',
  menuItems = DEFAULT_MENU_ITEMS
}) => {
  const isNarrow = useIsNarrow();
  const [isHovered, setIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [submenuTop, setSubmenuTop] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate submenu position
  const calculateSubmenuPosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setSubmenuTop(rect.bottom + 8); // 8px below the component
    }
  };

  useEffect(() => {
    if (isOpen) {
      calculateSubmenuPosition();
    }
  }, [isOpen]);

  const handleClick = () => {
    if (!isOpen) {
      calculateSubmenuPosition();
    }
    setIsOpen(!isOpen);
  };

  const handleSubmenuSelect = (item: SubmenuItem) => {
    setIsOpen(false);

    switch (item.id) {
      case 'profile':
        onProfileClick?.();
        break;
      case 'settings':
        onSettingsClick?.();
        break;
      case 'signout':
        onSignOutClick?.();
        break;
    }
  };

  // Close submenu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const MENU_ITEM_DEFINITIONS: Record<ActiveUserMenuItemId, SubmenuItem> = {
    profile: {
      id: 'profile',
      label: 'Profile',
      value: 'profile',
      icon: <ProfileIcon strokeWidth={1.5} />
    },
    settings: {
      id: 'settings',
      label: 'Settings',
      value: 'settings',
      icon: <SettingTwoIcon strokeWidth={1.5} />
    },
    signout: {
      id: 'signout',
      label: 'Sign Out',
      value: 'signout',
      icon: <LogoutIcon strokeWidth={1.5} />
    }
  };

  // Order comes from the caller's array, so a subset keeps its own sequence.
  // The default array reproduces the original literal exactly.
  const submenuItems: SubmenuItem[] = menuItems.map((id) => MENU_ITEM_DEFINITIONS[id]);

  const containerStyle: React.CSSProperties = {
    height: 'auto',
    borderRadius: avatarVariant === 'company' ? COMPANY_HOVER_RADIUS : '32px',
    display: 'flex',
    alignItems: 'center',
    gap: designTokens['spacing/twelve'] || '12px',
    paddingRight: designTokens['spacing/eight'] || '8px',
    backgroundColor: isHovered ? (designTokens.surface?.greyscale?.mist || '#f3f4f6') : 'transparent',
    cursor: 'pointer',
    transition: 'background-color 200ms ease',
    position: 'relative',
  };

  const textContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: 1,
    minWidth: 0, // Allow text truncation
  };

  const userNameStyle: React.CSSProperties = {
    fontFamily: designTokens.font?.family?.primary || 'Montserrat, system-ui, sans-serif',
    fontWeight: designTokens.font?.weight?.semibold || '600',
    fontSize: designTokens.font?.size?.sm || '0.8125rem',
    lineHeight: '1rem',
    color: designTokens.text?.primary || '#111827',
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'visible',
    textOverflow: 'ellipsis',
  };

  const companyNameStyle: React.CSSProperties = {
    fontFamily: designTokens.font?.family?.primary || 'Montserrat, system-ui, sans-serif',
    fontWeight: designTokens.font?.weight?.medium || '500',
    fontSize: designTokens.font?.size?.xs || '0.75rem',
    lineHeight: '0.875rem',
    color: designTokens.text?.secondary || '#6b7280',
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'visible',
    textOverflow: 'ellipsis',
  };

  const iconStyle: React.CSSProperties = {
    color: isHovered ? (designTokens.icon?.brand?.primary || '#10b981') : (designTokens.icon?.primary || '#343a40'),
    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 200ms ease, color 200ms ease',
  };

  return (
    <>
      <div
        ref={containerRef}
        className={className}
        style={containerStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleClick}
      >
        <Avatar
          imageUrl={userImageUrl}
          firstName={firstName}
          lastName={lastName}
          companyName={avatarVariant === 'company' ? userName : undefined}
          variant={avatarVariant}
          alt={`${userName} avatar`}
          size="medium"
        />

        {!isNarrow && (
          <div style={textContainerStyle}>
            <div style={userNameStyle}>{userName}</div>
            {/* Guarded rather than unconditional: an empty string used to render
                an empty row that reads as a stray gap. The app's only other
                caller (TopNavigationBar) always passes a real tenant name, so
                this is unreachable there — it matters for the portal, where a
                buyer with no company leads with their email and has no second
                line to show. */}
            {companyName && <div style={companyNameStyle}>{companyName}</div>}
          </div>
        )}
        
        <ChevronDown size={20} style={iconStyle} />
      </div>

      {/* Submenu */}
      <Submenu
        items={submenuItems}
        onSelect={handleSubmenuSelect}
        variant="dropdown"
        showCheckmarks={false}
        showIcons={true}
        isOpen={isOpen}
        usePortal={true}
        triggerElement={containerRef.current}
        position="bottom"
        constrainWidth={true}
        alignToRight={false}
        matchParentWidth={false}
        width="224px"
      />
    </>
  );
};

export default ActiveUser;